import { useApiRoutes } from "src/components/reusable/hooks/useApiRoutes";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import { useGenericErrorModal } from "src/components/reusable/hooks/useGenericErrorModal";
import { createEventAttendancePayload } from "src/utils/api.utils";
import { TEventAttendance } from "./types.event-attendance";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { updateEventsWithAttendance } from "./utils.event-attendance";
import { TEvent } from "src/shared/types/globalStotre.types";
import { toast } from "react-toastify";
import { throttleTime } from "src/utils/utils";

const DEFAULT_OPT_IN_SUCCESS_MESSAGE = "Your attendance has been updated successfully.";
const DEFAULT_OPT_OUT_SUCCESS_MESSAGE = "Your attendance has been cancelled successfully.";

export function useEventAttendance({ event }: { event?: TEvent } = {}) {
  const teamConfig = useTeamConfig();
  const { HOME_PAGE_SETTINGS } = teamConfig;
  const successToastConfig = HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.ATTENDANCE_SUCCESS_TOAST; // prettier-ignore
  const isReservationEnabled = HOME_PAGE_SETTINGS?.EVENT_CARD?.IS_RESERVATION_ENABLED || false; // prettier-ignore
  const { events, setGlobalState } = useGlobalStore();
  const { updateAttendanceApi, updateAttendanceWithAssignmentApi, getUpcomingEventsApi } = useApiRoutes(); // prettier-ignore
  const { fireErrorModal } = useGenericErrorModal();

  const updateStoreEvents = (attendance: TEventAttendance) => {
    const eventsUpdated = updateEventsWithAttendance({
      events,
      attendance,
      isReservationEnabled,
    });
    setGlobalState({ events: eventsUpdated });
  };

  const refreshEventsFromApi = async () => {
    try {
      let queryParams = {};
      if (isReservationEnabled) queryParams = { sortBy: "opt_ins_first" };
      const response = await getUpcomingEventsApi.get({ queryParams });
      const freshEvents = response.sortedEvents;
      const optedInCountForEventReservation = response.optedInCountForEventReservation ?? null;
      const reservationLimitsByPass = response.reservationLimitsByPass ?? null;
      setGlobalState({ events: freshEvents, optedInCountForEventReservation, reservationLimitsByPass });
    } catch {
      // silent — the optimistic update already happened
    }
  };

  const secondaryConfirmationSuccessMessage = HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.SECONDARY_CONFIRMATION_SUCCESS_MESSAGE; // prettier-ignore

  const fireAttendanceSuccessToast = (attendance: TEventAttendance) => {
    if (attendance?.isSecondaryConfirmation && attendance?.optInResponse) {
      if (secondaryConfirmationSuccessMessage) {
        toast.success(secondaryConfirmationSuccessMessage);
      }
      return;
    }

    const isAttending = attendance?.optInResponse;
    if (isAttending) {
      toast.success(successToastConfig?.OPT_IN_SUCCESS_MESSAGE || DEFAULT_OPT_IN_SUCCESS_MESSAGE);
    } else {
      toast.success(successToastConfig?.OPT_OUT_SUCCESS_MESSAGE || DEFAULT_OPT_OUT_SUCCESS_MESSAGE);
    }
  };

  const updateEventAttendance = async (attendance: TEventAttendance) => {
    try {
      const payload = createEventAttendancePayload({
        attendance,
        isReservationEnabled,
      });

      const isSecondaryConfirm = attendance.isSecondaryConfirmation && attendance.optInResponse;
      if (isReservationEnabled && !isSecondaryConfirm) {
        await updateAttendanceWithAssignmentApi.post(payload);
      } else {
        await updateAttendanceApi.post(payload);
      }

      // Awaited on purpose: `updateStoreEvents` patches a *stale* copy of
      // `events` captured at render time, so if it lands after the refetch it
      // clobbers the freshly sorted (opt-ins-first) list and the event the user
      // just claimed never moves up to the featured slot.
      await throttleTime(400, () => {
        updateStoreEvents(attendance);
        fireAttendanceSuccessToast(attendance);
      });

      // Refetch events to get fresh optedInCountForEventReservation from backend
      await refreshEventsFromApi();
    } catch (err) {
      fireErrorModal(err);
    }
  };

  return {
    isReservationEnabled,
    updateEventAttendance,
  };
}
